import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismNumberInput, PrismTooltip } from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import HotelBudgetForecastTableTdWithVariance from 'lumen/components/hotel/budget-forecast/table/td-with-variance';
import type { Variance } from 'lumen/utils/constants/budget-forecast-constants';
import { localCopy } from 'tracked-toolbox';

import styles from './td-number.module.css';

interface SettingsBudgetForecastTableBudgetForecastTdNumberSignature {
  Args: {
    cellData: {
      availableInventory?: number | null;
      hasUnsavedChanges?: boolean;
      isDisabled?: boolean;
      onChange: (value: number) => void;
      value: number;
      varianceType: Variance;
      varianceValue: number | null;
    };
    row: {
      isDisabled?: boolean;
      isEditable?: boolean;
    };
  };
}

export default class SettingsBudgetForecastTableBudgetForecastTdNumber extends Component<SettingsBudgetForecastTableBudgetForecastTdNumberSignature> {
  @service declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked isFocused: boolean = false;

  @localCopy('args.cellData.value') declare tempValue: number | null;

  get inputHasWarning(): boolean {
    const { inventoryExcessAmount } = this;

    if (inventoryExcessAmount === undefined) {
      return false;
    }

    return inventoryExcessAmount > 0;
  }

  get inputIcon():
    | {
        name: 'warning';
      }
    | undefined {
    if (!this.inputHasWarning) {
      return undefined;
    }

    return {
      name: 'warning',
    };
  }

  get inventoryExcessAmount(): number | undefined {
    const { cellData } = this.args;
    const { tempValue } = this;

    if (
      cellData.availableInventory === undefined ||
      cellData.availableInventory === null ||
      !tempValue
    ) {
      return undefined;
    }

    return tempValue - cellData.availableInventory;
  }

  get isDisabled(): boolean {
    const { cellData, row } = this.args;

    return Boolean(cellData.isDisabled || row.isDisabled);
  }

  get isEditable(): boolean {
    return this.args.row.isEditable ?? true;
  }

  get showUnsavedChangesBullet(): boolean {
    if (this.isFocused || this.isDisabled) {
      return false;
    }

    return Boolean(this.args.cellData.hasUnsavedChanges);
  }

  setValue(): void {
    const { cellData } = this.args;

    if (this.tempValue === null) {
      this.tempValue = 0;
    }

    cellData.onChange(this.tempValue);
  }

  @action onBlur(): void {
    this.isFocused = false;

    if (this.tempValue !== this.args.cellData.value) {
      this.setValue();
    }
  }

  @action onFocus(): void {
    this.isFocused = true;
  }

  @action onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.setValue();
    }
  }

  @action onValueChange(value: number | null): void {
    this.tempValue = value;
  }

  <template>
    {{#if this.isEditable}}
      <div class="ic-product-tour-49285022-number-input relative">
        <PrismNumberInput
          @disabled={{this.isDisabled}}
          @hasWarnings={{this.inputHasWarning}}
          @iconAfter={{this.inputIcon}}
          @isSmall={{true}}
          @locale={{this.hotelAndMarketService.user.locale}}
          @min={{0}}
          @onValueChange={{this.onValueChange}}
          @removePadding={{true}}
          @step={{0.01}}
          @value={{this.tempValue}}
          data-test-id="budget-forecast-td-number-editable"
          {{on "blur" this.onBlur}}
          {{on "focus" this.onFocus}}
          {{on "keydown" this.onKeyDown}}
        />
        {{#if (and this.inventoryExcessAmount this.inputHasWarning)}}
          <PrismTooltip
            @side="top"
            @text={{t
              "settings.budget_forecast.inventory_excl_ooo.warning_message.count"
              count=(formatNumber
                this.inventoryExcessAmount
                maximumFractionDigits=0
                minimumFractionDigits=0
              )
            }}
          />
        {{/if}}
        {{#if this.showUnsavedChangesBullet}}
          <div class={{styles.edited-value-bullet}}></div>
        {{/if}}
      </div>
    {{else}}
      <HotelBudgetForecastTableTdWithVariance
        @cellData={{hash
          value=@cellData.value
          varianceType=@cellData.varianceType
          varianceValue=@cellData.varianceValue
        }}
        data-test-id="budget-forecast-td-number-non-editable"
      />
    {{/if}}
  </template>
}
